import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Youtube, PlayIcon, ArrowUpRightIcon } from 'lucide-react';
// ─────────────────────────────────────────────────────────────
// CONFIGURACIÓN — reemplaza estos valores con los datos reales de tu canal:
//   • CHANNEL_HANDLE: tu handle de YouTube (ej. "@creceelite")
//   • CHANNEL_URL: link al canal completo
//   • FEATURED_VIDEO_ID: ID del video principal (la parte después de "v=" en la URL)
//   • SECONDARY_VIDEOS: lista de IDs adicionales con sus títulos
// Para encontrar el ID de un video: en la URL https://youtu.be/XXXXX o
// https://www.youtube.com/watch?v=XXXXX, el ID es la parte XXXXX.
// ─────────────────────────────────────────────────────────────
const CHANNEL_HANDLE = '@creceelite';
const CHANNEL_URL = 'https://www.youtube.com/@creceelite';
const FEATURED_VIDEO_ID = ''; // ej. 'dQw4w9WgXcQ'
const SECONDARY_VIDEOS: Array<{
  id: string;
  title: string;
}> = [{
  id: '',
  title: 'Tu propósito está más cerca de lo que crees'
}, {
  id: '',
  title: 'Los 5 fundamentos para una vida con propósito'
}, {
  id: '',
  title: 'Cómo construir disciplina inquebrantable'
}];
function youtubeEmbedUrl(videoId: string) {
  return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`;
}
function youtubeThumbUrl(videoId: string) {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}
export function VideoShowcase() {
  const {
    ref,
    isVisible
  } = useScrollReveal({
    threshold: 0.1
  });
  const hasFeatured = FEATURED_VIDEO_ID.length > 0;
  return <section id="videos" className="py-20 md:py-36 bg-richBlack relative overflow-hidden border-t border-white/5" data-id="element-290">
      {/* Ambient gold glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gold/[0.04] rounded-full blur-[140px] pointer-events-none" data-id="element-291" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-292">
        {/* Header */}
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 40
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 1,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center mb-12 md:mb-20" data-id="element-293">
          <div className="flex items-center justify-center gap-4 mb-6" data-id="element-294">
            <div className="w-8 h-[1px] bg-gold" data-id="element-295" />
            <span className="font-montserrat text-gold tracking-[0.3em] text-xs uppercase" data-id="element-296">
              En YouTube
            </span>
            <div className="w-8 h-[1px] bg-gold" data-id="element-297" />
          </div>

          <h2 className="font-montserrat font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-[0.06em] mb-6 leading-tight" data-id="element-298">
            <span className="text-warmWhite" data-id="element-299">Contenido para </span>
            <span className="text-gold italic font-bold" data-id="element-300">elevarte</span>
          </h2>

          <p className="font-montserrat text-base md:text-lg text-mutedGray font-light max-w-2xl mx-auto leading-relaxed" data-id="element-301">
            Videos, enseñanzas y reflexiones para acompañarte en tu camino de
            transformación y propósito.
          </p>
        </motion.div>

        {/* Featured Video */}
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 1,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-12 md:mb-16" data-id="element-302">
          <div className="relative max-w-5xl mx-auto" data-id="element-303">
            {/* Gold glow behind video */}
            <div className="absolute -inset-2 md:-inset-4 bg-gold/[0.08] blur-2xl rounded-2xl pointer-events-none" data-id="element-304" />

            <div className="relative aspect-video w-full bg-zinc-900 border border-gold/20 overflow-hidden shadow-[0_0_60px_rgba(201,168,76,0.15)]" data-id="element-305">
              {hasFeatured ? <iframe src={youtubeEmbedUrl(FEATURED_VIDEO_ID)} title="Video destacado de CRECEELITE" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-306" /> : <VideoPlaceholder size="large" title="Próximamente: video destacado" data-id="element-307" />}
            </div>
          </div>
        </motion.div>

        {/* Secondary Videos Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-12 md:mb-16" data-id="element-308">
          {SECONDARY_VIDEOS.map((video, index) => <motion.article key={`${video.title}-${index}`} initial={{
          opacity: 0,
          y: 40
        }} animate={isVisible ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.9,
          delay: 0.4 + index * 0.12,
          ease: [0.16, 1, 0.3, 1]
        }} className="group" data-id="element-309">
              <div className="relative aspect-video w-full bg-zinc-900 border border-white/5 group-hover:border-gold/40 overflow-hidden transition-all duration-500" data-id="element-310">
                {video.id ? <iframe src={youtubeEmbedUrl(video.id)} title={video.title} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-311" /> : <VideoPlaceholder size="small" data-id="element-312" />}
              </div>

              <h3 className="font-montserrat font-medium text-warmWhite text-sm md:text-base mt-4 leading-snug group-hover:text-gold transition-colors duration-300" data-id="element-313">
                {video.title}
              </h3>
            </motion.article>)}
        </div>

        {/* Channel CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 1,
        delay: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-col items-center" data-id="element-314">
          <div className="w-12 h-[1px] bg-gold/40 mb-8" data-id="element-315" />

          <a href={CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center gap-3 px-8 py-4 border border-gold/40 text-gold font-montserrat font-semibold tracking-wide hover:bg-gold hover:text-richBlack hover:border-gold transition-all duration-300 text-sm md:text-base" data-id="element-316">
            <Youtube className="w-5 h-5" data-id="element-317" />
            Ver el canal completo
            <ArrowUpRightIcon className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" data-id="element-318" />
          </a>

          <p className="font-montserrat text-xs text-mutedGray tracking-widest uppercase mt-5" data-id="element-319">
            {CHANNEL_HANDLE}
          </p>
        </motion.div>
      </div>
    </section>;
}
// Elegant placeholder shown when a video ID hasn't been set yet.
function VideoPlaceholder({
  size,
  title
}: {
  size: 'large' | 'small';
  title?: string;
}) {
  return <a href={CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-zinc-900 via-richBlack to-zinc-900 group/ph" aria-label="Visitar canal de YouTube" data-id="element-320">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(201,168,76,0.08),_transparent_70%)] pointer-events-none" data-id="element-321" />

      <div className={`relative flex items-center justify-center rounded-full bg-gold/10 border border-gold/30 group-hover/ph:bg-gold/20 group-hover/ph:scale-105 transition-all duration-500 ${size === 'large' ? 'w-20 h-20 md:w-24 md:h-24' : 'w-14 h-14'}`} data-id="element-322">
        <PlayIcon className={`text-gold fill-gold ${size === 'large' ? 'w-8 h-8 md:w-10 md:h-10' : 'w-5 h-5'} ml-1`} data-id="element-323" />
      </div>

      {title && <p className={`font-montserrat text-mutedGray font-light text-center px-6 mt-5 ${size === 'large' ? 'text-sm md:text-base' : 'text-xs'}`} data-id="element-324">
          {title}
        </p>}

      <span className="font-montserrat text-[10px] text-gold tracking-widest uppercase mt-3" data-id="element-325">
        Ver en YouTube
      </span>
    </a>;
}